import React, {useState, useEffect} from 'react';
import {
  useLocation,
  useMatch,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import classNames from 'classnames';
import _ from 'lodash';
import {FormikValues, useFormik} from 'formik';
import FieldInput from '../../../components/FieldInput/FieldInput';
import FieldSelect from '../../../components/FieldSelect/FieldSelect';
import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb';
import ToastMessage from '../../../components/ToastMessage/ToastMessage';
import FieldRelatedSelect from '../../../components/FieldRelatedSelect/FieldRelatedSelect';
import validateEmpty from '../../../utils/validateEmpty';
import {
  AssetField,
  IErrorBase,
  IToastMessage,
  ReduxState,
  SelectOption,
  State,
} from '../../../interface';
import style from './InventorySetup.module.scss';
import {
  getAllAssessors,
  getAllAssetClass,
  getAllLegalEntities,
  getAllProperties,
  getAsset,
  getCompanies,
  getUploadFile,
  setAssets,
  updateAsset,
  updateUploadFile,
} from '../../../services';
import CustomCheckbox from '../../../components/CustomCheckbox/CustomCheckbox';
import FieldInputWithOptions from '../../../components/FieldInputWithOptions/FieldInputWithOptions';
import removeAssetsEmptyFields from '../../../utils/removeAssetsEmptyFields';
import {AxiosResponse} from 'axios';
import {connect} from 'react-redux';
import {useDebounce} from 'use-debounce';
import Loader, {LoaderIndicator} from '../../../components/Loader/Loader';
import DiscardChangeConfirmModal from '../../../components/DiscardChangeConfirmModal/DiscardChangeConfirmModal';
import TooltipWrapper from '../../../components/TooltipWrapper/TooltipWrapper';
import {TOOLTIP_SECTIONS} from '../../../enums';
import {useAppDispatch} from '../../../redux/store';
import {getDashboardInfo} from '../../../redux/features/dashboard-info-slice';
import useDashboardInfo from '../../../hook/useDashboardInfo';
import useCommonEffects from '../../../utils/assetSetup';
import useSelectedSystemTaxYear from '../../../hook/useSelectedSystemTaxYear';
import moment from 'moment';

// Init formik values
const newInit = {
  showOptional: false,
  disposed: false,
  isAddition: false,
  companyId: null,
  legalEntityId: null,
  propertyId: null,
  assessorAccountId: null,
  number: '',
  class: '',
  acquisitionDate: moment(),
  taxYear: null,
  alternateAssetId: '',
  description: '',
  notes: '',
  type: 'Inventory',
  stateId: null,
  inventory: {
    januaryAmount: null,
    februaryAmount: null,
    marchAmount: null,
    aprilAmount: null,
    mayAmount: null,
    juneAmount: null,
    julyAmount: null,
    augustAmount: null,
    septemberAmount: null,
    octoberAmount: null,
    novemberAmount: null,
    decemberAmount: null,
  },
};

// Define the required fields
const requiredField = [
  'stateId',
  'companyId',
  'legalEntityId',
  'propertyId',
  'assessorAccountId',
  'number',
  'class',
  // 'acquisitionDate',
  'taxYear',
];

const decimalField = [
  'taxYear',
  'inventory.januaryAmount',
  'inventory.februaryAmount',
  'inventory.marchAmount',
  'inventory.aprilAmount',
  'inventory.mayAmount',
  'inventory.juneAmount',
  'inventory.julyAmount',
  'inventory.augustAmount',
  'inventory.septemberAmount',
  'inventory.octoberAmount',
  'inventory.novemberAmount',
  'inventory.decemberAmount',
];

interface Props {
  states: State[];
}

function setOptions(d: AxiosResponse) {
  return _.map(d.data.items, (item) => ({
    value: item.id,
    label: item.name,
    name: item.number,
    item: item,
  }));
}

function InventorySetup(props: Props) {
  const appDispatch = useAppDispatch();
  const {id} = useParams();
  const {states} = props;
  const uploadMatch = useMatch('/assets/editUploadInventorySetup/*');
  const [searchParams] = useSearchParams();
  const assetId = searchParams.get('itemId');
  const location = useLocation();
  // Breadcrumb, dashboard depend on user skip or not
  const breadcrumb = !id
    ? [
        {
          value: 'Assets',
          href: '/assets',
        },
        {value: 'Add Asset', href: '/assets/assetsUpload'},
        {value: 'Add Inventory Asset'},
      ]
    : [
        {
          value: 'Assets',
          href: '/assets',
        },
        {value: 'Add Asset', href: '/assets/assetsUpload'},
        {value: 'Edit Inventory Asset'},
      ];

  // Options of select fields
  const [companies, setCompanies] = useState<SelectOption[]>([]);
  const [legalEntities, setlegalEntities] = useState<SelectOption[]>([]);
  const [properties, setPropertie] = useState<SelectOption[]>([]);
  const [assessors, setAssessors] = useState<SelectOption[]>([]);
  const [assetclasses, setassetclasses] = useState<SelectOption[]>([]);
  const [fetchingCompanies, setFetchingCompanies] = useState(false);
  const [fetchingAllLegalEntities, setFetchingAllLegalEntities] =
    useState(false);
  const [fetchingAllProperties, setFetchingAllProperties] = useState(false);
  const [fetchingAllAssessors, setFetchingAllAssessors] = useState(false);
  const [navBlock, setNavBlock] = useState<boolean>();
  const selectedSystemTaxYear = useSelectedSystemTaxYear();

  // Get options of select from JSON
  useEffect(() => {
    setFetchingCompanies(true);
    getCompanies()
      .then((d) => {
        setCompanies(setOptions(d));
        setFetchingCompanies(false);
      })
      .catch(() => {
        setFetchingCompanies(false);
      });

    setFetchingAllLegalEntities(true);
    getAllLegalEntities()
      .then((d) => {
        setlegalEntities(setOptions(d));
        setFetchingAllLegalEntities(false);
      })
      .catch(() => {
        setFetchingAllLegalEntities(false);
      });

    setFetchingAllProperties(true);
    getAllProperties()
      .then((d) => {
        setPropertie(setOptions(d));
        setFetchingAllProperties(false);
      })
      .catch(() => {
        setFetchingAllProperties(false);
      });

    setFetchingAllAssessors(true);
    getAllAssessors(selectedSystemTaxYear?.taxYear || '')
      .then((d) => {
        setAssessors(
          _.map(d.data.items, (item) => ({
            value: item.id,
            label: item.assessor.name,
            name: item.number,
            item: item,
          })),
        );
        setFetchingAllAssessors(false);
      })
      .catch(() => {
        setFetchingAllAssessors(false);
      });
  }, []);

  const navigate = useNavigate();

  // Flag for submit or not
  const [issubmit, setissubmit] = useState<boolean>(false);
  const [submitToast, setSubmitToast] = useState<boolean>(false);
  const [isSaveReset, setIsSaveReset] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [selectedSubmitButton, setSelectedSubmitButton] = useState('');

  function handleError(e: AxiosResponse<IErrorBase>) {
    setToastMessage({
      visible: true,
      message: e?.data.errors.length
        ? e?.data.errors[0].replace(/items\.\d\./, '')
        : `Error occurs, please try again later.`,
      type: 'error',
    });
    setSubmitting(false);
  }

  const { taxYear: currentYear } = useDashboardInfo();

  const formik = useFormik<FormikValues>({
    initialValues: newInit,
    enableReinitialize: true,
    validate: (values) => {
      // Validate form,if Same As Mailing Address checkbox is checked,not validate the package fields
      if (issubmit) {
        let errors = validateEmpty(_.pick(values, requiredField));
        if (
          values.stateId &&
          propertyStateId &&
          !_.isEqual(values.stateId, propertyStateId)
        ) {
          errors = _.assign({}, errors, {
            stateId: `Must be the same as Property's state: ${
              _.find(states, ['id', propertyStateId])?.abbreviation
            }`,
          });
        }
        if (
          values.taxYear &&
          !_.isEqual(_.toString(values.taxYear), _.toString(currentYear))
        ) {
          errors = _.assign(errors, {
            taxYear: `The tax year must be ${currentYear}`,
          });
        }
        const result = _.every(_.values(errors), (e: string | []) => {
          return _.every(e, _.isEmpty);
        });
        if (!result) {
          if (submitToast) {
            setSubmitToast(false);
            setToastMessage({
              visible: true,
              message:
                'The Asset you save had some errors that need to be fixed. Please address these to move forward.',
              type: 'error',
            });
          }
          return errors;
        }
      }
    },
    onSubmit: () => {
      setNavBlock(false);
      const result = _.cloneDeep(formik.values);
      _.map(decimalField, (item) =>
        _.update(result, item, (v) => _.toNumber(v)),
      );
      if (id) {
        if (uploadMatch) {
          setSubmitting(true);
          updateUploadFile(id, {
            items: _.unionBy(
              _.concat(
                removeAssetsEmptyFields(
                  _.assign(result, {
                    companyName: _.find(companies, ['value', result.companyId])
                      ?.label,
                    companyNumber: _.find(companies, [
                      'value',
                      result.companyId,
                    ])?.name,
                    legalEntityName: _.find(legalEntities, [
                      'value',
                      result.legalEntityId,
                    ])?.label,
                    legalEntityNumber: _.find(legalEntities, [
                      'value',
                      result.legalEntityId,
                    ])?.name,
                    propertyName: _.find(properties, [
                      'value',
                      result.propertyId,
                    ])?.label,
                    propertyNumber: _.find(properties, [
                      'value',
                      result.propertyId,
                    ])?.name,
                    assessor: _.find(assessors, ['value', result.assessorAccountId])
                      ?.label,
                    assessorAccountNumber: _.find(assessors, [
                      'value',
                      result.assessorAccountId,
                    ])?.name,
                    state: _.find(states, ['id', result.stateId])?.abbreviation,
                  }),
                ),
                items,
              ),
              'id',
            ),
          })
            .then((e: AxiosResponse<IErrorBase>) => {
              if (_.inRange(e.status, 200, 299)) {
                navigate(NAVIGATE);
              } else {
                setToastMessage({
                  visible: true,
                  message: `Error occurs, please try again later.`,
                  type: 'error',
                });
              }
              setSubmitting(false);
            })
            .catch((e) => {
              handleError(e);
            });
        } else {
          setSubmitting(true);
          updateAsset(
            id,
            _.omit(
              removeAssetsEmptyFields(result),
              'legalEntityId',
              'propertyId',
            ),
          )
            .then((e: AxiosResponse<IErrorBase>) => {
              if (_.inRange(e.status, 200, 299)) {
                appDispatch(getDashboardInfo());
                navigate(NAVIGATE);
              } else {
                setToastMessage({
                  visible: true,
                  message: `Error occurs, please try again later.`,
                  type: 'error',
                });
              }
              setSubmitting(false);
            })
            .catch((e) => {
              handleError(e);
            });
        }
      } else {
        setSubmitting(true);
        setAssets({
          items: [
            _.omit(
              removeAssetsEmptyFields(result),
              'legalEntityId',
              'propertyId',
            ),
          ],
        })
          .then((e: AxiosResponse<IErrorBase>) => {
            if (_.inRange(e.status, 200, 299)) {
              if (isSaveReset) {
                formik.resetForm();
                setToastMessage({
                  visible: true,
                  message: `Add Asset successfully`,
                  type: 'success',
                });
                appDispatch(getDashboardInfo());
                setIsSaveReset(false);
                setissubmit(false);
                setSubmitToast(false);
              } else {
                appDispatch(getDashboardInfo());
                navigate(NAVIGATE);
              }
            } else {
              setToastMessage({
                visible: true,
                message: `Error occurs, please try again later.`,
                type: 'error',
              });
            }
            setSubmitting(false);
          })
          .catch((e) => {
            handleError(e);
          });
      }
    },
  });

  let NAVIGATE = '';
  let assetOriginValue = '';
  if (!id) {
    NAVIGATE = `/assets/assetsUpload`;
    assetOriginValue = 'Manually';
  } else {
    if (uploadMatch) {
      NAVIGATE = `/assets/assetsReview/${id}`;
      assetOriginValue = 'Import';
    } else {
      assetOriginValue = formik.values.origin;
      if (location.state) {
        NAVIGATE = location.state as string;
      } else {
        NAVIGATE = `/assets?assetTab=Inventory`;
      }
    }
  }

  const [debounce] = useDebounce(formik.values.class, 400);
  useEffect(() => {
    getAllAssetClass({page: 1, perPage: 20, text: debounce}).then((d) =>
      setassetclasses(setOptions(d)),
    );
  }, [debounce]);
  // reset valid by click the close icon of error message box
  const [toastMessage, setToastMessage] = useState<IToastMessage>();

  //Edit
  const [asset, setAsset] = useState<AssetField>();
  const [items, setItems] = useState<AssetField[]>([]);
  const [fetchingAsset, setFetchingAsset] = useState(false);
  useEffect(() => {
    if (id) {
      if (uploadMatch) {
        setFetchingAsset(true);
        getUploadFile(id)
          .then((d) => {
            setItems(d.data.items);
            const result = _.find(d.data.items, (item) =>
              _.isEqual(_.toString(item['id']), assetId),
            );
            setAsset(_.mapValues(result));
            setPropertyStateId(
              _.get(
                _.find(properties, ['value', result.propertyId]),
                'item.address.stateId',
              ),
            );
            setTimeout(() => {
              setissubmit(true);
            }, 500);

            setFetchingAsset(false);
          })
          .catch(() => {
            setFetchingAsset(false);
          });
      } else {
        setFetchingAsset(true);
        getAsset(id)
          .then((d) => {
            const result = _.assign(d.data, {
              propertyId: d.data.assessorAccount?.property?.id,
              legalEntityId: d.data.assessorAccount?.property?.legalEntity?.id,
              taxYear: _.toString(d.data.taxYear),
            });
            
            setAsset(
              _.assign(d.data, {
                propertyId: d.data.assessorAccount?.property?.id,
                legalEntityId: d.data.assessorAccount?.property?.legalEntity?.id,
                taxYear: _.toString(d.data.taxYear),
              }),
            );
            setPropertyStateId(result.assessorAccount?.property?.address?.stateId);

            setFetchingAsset(false);
          })
          .catch(() => {
            setFetchingAsset(false);
          });
      }
    }
  }, [id, uploadMatch, properties]);

  const [statincorporation, setstatincorporation] = useState<SelectOption[]>([],);
  const [propertyStateId, setPropertyStateId] = useState();
  const [currentSelected, setCurrentSelected] = useState<string>('company');

  useCommonEffects(asset, states, companies, properties, legalEntities, requiredField, formik,
    setstatincorporation, setPropertyStateId, setNavBlock, newInit, currentSelected);

  return (
    <>
      <div className={style['main-content']}>
        {/* Braedcrumb */}
        <Breadcrumb items={breadcrumb} />
        {/* Error message box,if there are errors message */}
        {toastMessage?.visible && (
          <div className={style['toast']}>
            <ToastMessage
              status={toastMessage}
              className={toastMessage?.type ? toastMessage?.type : ''}
            />
          </div>
        )}
        <div className={style['header']}>
          <h2>
            {!id ? (
              <TooltipWrapper
                tooltipSection={TOOLTIP_SECTIONS.PageTitle}
                tooltipKey='Add Inventory Asset'
              >
                <span>Add Inventory Asset</span>
              </TooltipWrapper>
            ) : (
              <TooltipWrapper
                tooltipSection={TOOLTIP_SECTIONS.PageTitle}
                tooltipKey='Edit Inventory Asset'
              >
                <span>Edit Inventory Asset</span>
              </TooltipWrapper>
            )}
          </h2>
          <div className={style['addition-disposal-checkboxes']}>
            <CustomCheckbox
              classnames={style['margin-right']}
              label='Addition'
              value={formik.values.isAddition}
              onChange={(value) => {
                formik.setFieldValue(`isAddition`, value);
              }}
            />
            <CustomCheckbox
              label='Disposed'
              value={formik.values.disposed}
              onChange={(value) => {
                formik.setFieldValue(`disposed`, value);
              }}
            />
          </div>
        </div>
        <form className={style['main']} onSubmit={formik.handleSubmit}>
          <div className={classNames(style['asset-section'])}>
            <div className={style['section']}>
              {/* Company form list */}
              <div className={style['form']}>
                <fieldset>
                  <div className={style['row']}>
                    <div className={style['column']}>
                      <FieldSelect
                        labelText='Company Name'
                        selectId={formik.values.companyId}
                        options={companies}
                        onSelect={(value) => {
                          formik.setFieldValue(
                            `companyId`,
                            value?.value ? _.toNumber(value?.value) : null,
                          );
                          setCurrentSelected('company');
                        }}
                        error={
                          formik.errors &&
                          (formik.errors as FormikValues).companyId
                        }
                        required
                      />
                    </div>
                    <div className={style['column']}>
                      <FieldRelatedSelect
                        value={formik.values.legalEntityId}
                        labelText='Legal Entity Name & Number'
                        options={legalEntities}
                        onChange={(legalEntityId) => {
                          formik.setFieldValue('legalEntityId', legalEntityId);
                          setCurrentSelected('owner');
                        }}
                        error={
                          formik.errors &&
                          (formik.errors as FormikValues).legalEntityId
                        }
                        required
                      />
                    </div>
                    <div className={style['column']}>
                      <FieldRelatedSelect
                        labelText='Property Name & Number'
                        value={formik.values.propertyId}
                        options={
                          formik.values.legalEntityId
                            ? _.filter(properties, property => property?.item?.legalEntityId == formik.values.legalEntityId)
                            : []
                        }
                        onChange={(propertyId) => {
                          formik.setFieldValue('propertyId', propertyId);
                          setCurrentSelected('property');
                        }}
                        error={
                          formik.errors &&
                          (formik.errors as FormikValues).propertyId
                        }
                        required
                      />
                    </div>
                  </div>
                  <div className={style['row']}>
                    <div className={style['column']}>
                      <FieldRelatedSelect
                        labelText='Assessor & Assessor Account Number'
                        value={formik.values.assessorAccountId}
                        options={_.filter(assessors, assessor => assessor?.item?.propertyId == formik.values.propertyId)}
                        onChange={(assessorAccountId) => {
                          formik.setFieldValue('assessorAccountId',  _.toNumber(assessorAccountId));
                          setCurrentSelected('assessor');
                        }}
                        error={
                          formik.errors &&
                          (formik.errors as FormikValues).assessorAccountId
                        }
                        required
                      />
                    </div>
                    <div className={style['column']}>
                      <FieldInputWithOptions
                        labelText='Asset Class'
                        options={assetclasses}
                        value={formik.values.class}
                        onChange={(value) =>
                          formik.setFieldValue(`class`, value)
                        }
                        error={
                          formik.errors && (formik.errors as FormikValues).class
                        }
                        placeholder='Enter'
                        required
                      />
                    </div>
                    <div className={style['column']}>
                      <FieldInput
                        labelText='Asset Number'
                        value={formik.values.number}
                        onChange={(value) =>
                          formik.setFieldValue(`number`, value)
                        }
                        error={
                          formik.errors &&
                          (formik.errors as FormikValues).number
                        }
                        placeholder='Enter'
                        maxLength={50}
                        required
                      />
                    </div>
                  </div>
                  <div className={style['row']}>
                    <div className={classNames(style['column'])}>
                      <FieldSelect
                        labelText='State'
                        options={statincorporation}
                        selectId={formik.values.stateId}
                        onSelect={(value) =>
                          formik.setFieldValue(
                            `stateId`,
                            value?.value ? _.toNumber(value?.value) : null,
                          )
                        }
                        error={
                          formik.errors &&
                          (formik.errors as FormikValues).stateId
                        }
                        required
                      />
                    </div>
                    <div className={style['column']}>
                      <FieldInput
                        labelText='Tax Year'
                        value={formik.values.taxYear}
                        onChange={(value) =>
                          formik.setFieldValue(`taxYear`, value)
                        }
                        placeholder='Enter'
                        yearFormat
                        required
                        error={
                          formik.errors &&
                          (formik.errors as FormikValues).taxYear
                        }
                        maxLength={4}
                      />
                    </div>
                  </div>
                  <div className={style['row']}>
                    <div
                      className={classNames(
                        style['column'],
                        style['whole-column'],
                      )}
                    >
                      <FieldInput
                        labelText='Asset Description'
                        value={formik.values?.description}
                        onChange={(value) =>
                          formik.setFieldValue(`description`, value)
                        }
                        placeholder='Enter'
                        recommended
                        maxLength={100}
                      />
                    </div>
                  </div>
                  <div className={style['row']}>
                    <div
                      className={classNames(
                        style['column'],
                        style['small-column'],
                      )}
                    >
                      <FieldInput
                        labelText='January'
                        value={formik.values.inventory?.januaryAmount}
                        onChange={(value) =>
                          formik.setFieldValue(`inventory.januaryAmount`, value)
                        }
                        recommended
                        decimal
                      />
                    </div>
                    <div
                      className={classNames(
                        style['column'],
                        style['small-column'],
                      )}
                    >
                      <FieldInput
                        labelText='February'
                        value={formik.values.inventory?.februaryAmount}
                        onChange={(value) =>
                          formik.setFieldValue(
                            `inventory.februaryAmount`,
                            value,
                          )
                        }
                        recommended
                        decimal
                      />
                    </div>
                    <div
                      className={classNames(
                        style['column'],
                        style['small-column'],
                      )}
                    >
                      <FieldInput
                        labelText='March'
                        value={formik.values.inventory?.marchAmount}
                        onChange={(value) =>
                          formik.setFieldValue(`inventory.marchAmount`, value)
                        }
                        recommended
                        decimal
                      />
                    </div>
                    <div
                      className={classNames(
                        style['column'],
                        style['small-column'],
                      )}
                    >
                      <FieldInput
                        labelText='April'
                        value={formik.values.inventory?.aprilAmount}
                        onChange={(value) =>
                          formik.setFieldValue(`inventory.aprilAmount`, value)
                        }
                        recommended
                        decimal
                      />
                    </div>
                    <div
                      className={classNames(
                        style['column'],
                        style['small-column'],
                      )}
                    >
                      <FieldInput
                        labelText='May'
                        value={formik.values.inventory?.mayAmount}
                        onChange={(value) =>
                          formik.setFieldValue(`inventory.mayAmount`, value)
                        }
                        recommended
                        decimal
                      />
                    </div>
                    <div
                      className={classNames(
                        style['column'],
                        style['small-column'],
                      )}
                    >
                      <FieldInput
                        labelText='June'
                        value={formik.values.inventory?.juneAmount}
                        onChange={(value) =>
                          formik.setFieldValue(`inventory.juneAmount`, value)
                        }
                        recommended
                        decimal
                      />
                    </div>
                  </div>
                  <div className={style['row']}>
                    <div
                      className={classNames(
                        style['column'],
                        style['small-column'],
                      )}
                    >
                      <FieldInput
                        labelText='July'
                        value={formik.values.inventory?.julyAmount}
                        onChange={(value) =>
                          formik.setFieldValue(`inventory.julyAmount`, value)
                        }
                        recommended
                        decimal
                      />
                    </div>
                    <div
                      className={classNames(
                        style['column'],
                        style['small-column'],
                      )}
                    >
                      <FieldInput
                        labelText='August'
                        value={formik.values.inventory?.augustAmount}
                        onChange={(value) =>
                          formik.setFieldValue(`inventory.augustAmount`, value)
                        }
                        recommended
                        decimal
                      />
                    </div>
                    <div
                      className={classNames(
                        style['column'],
                        style['small-column'],
                      )}
                    >
                      <FieldInput
                        labelText='September'
                        value={formik.values.inventory?.septemberAmount}
                        onChange={(value) =>
                          formik.setFieldValue(
                            `inventory.septemberAmount`,
                            value,
                          )
                        }
                        recommended
                        decimal
                      />
                    </div>
                    <div
                      className={classNames(
                        style['column'],
                        style['small-column'],
                      )}
                    >
                      <FieldInput
                        labelText='October'
                        value={formik.values.inventory?.octoberAmount}
                        onChange={(value) =>
                          formik.setFieldValue(`inventory.octoberAmount`, value)
                        }
                        recommended
                        decimal
                      />
                    </div>
                    <div
                      className={classNames(
                        style['column'],
                        style['small-column'],
                      )}
                    >
                      <FieldInput
                        labelText='November'
                        value={formik.values.inventory?.novemberAmount}
                        onChange={(value) =>
                          formik.setFieldValue(
                            `inventory.novemberAmount`,
                            value,
                          )
                        }
                        recommended
                        decimal
                      />
                    </div>
                    <div
                      className={classNames(
                        style['column'],
                        style['small-column'],
                      )}
                    >
                      <FieldInput
                        labelText='December'
                        value={formik.values.inventory?.decemberAmount}
                        onChange={(value) =>
                          formik.setFieldValue(
                            `inventory.decemberAmount`,
                            value,
                          )
                        }
                        recommended
                        decimal
                      />
                    </div>
                  </div>
                </fieldset>
                {formik.values.showOptional && (
                  <fieldset>
                    <legend>
                      <TooltipWrapper
                        tooltipSection={TOOLTIP_SECTIONS.Common}
                        tooltipKey='Optional Information'
                      >
                        <span>Optional Information</span>
                      </TooltipWrapper>
                    </legend>
                    <div className={style['row']}>
                      <div className={style['column']}>
                        <FieldInput
                          labelText='Alternate Asset ID'
                          value={formik.values?.alternateAssetId}
                          onChange={(value) =>
                            formik.setFieldValue(`alternateAssetId`, value)
                          }
                          placeholder='Enter'
                          maxLength={50}
                        />
                      </div>
                      <div
                        className={classNames(
                          style['column'],
                          style['half-column'],
                        )}
                      >
                        <FieldInput
                          labelText='Notes'
                          value={formik.values?.notes}
                          onChange={(value) =>
                            formik.setFieldValue(`notes`, value)
                          }
                          placeholder='Enter'
                          maxLength={255}
                        />
                      </div>
                      <div
                        className={classNames(
                          style['column'],
                          style['small-column'],
                        )}
                      >
                        <FieldInput
                          labelText='Asset origin'
                          value={assetOriginValue}
                          onChange={(value) =>
                            formik.setFieldValue(`origin`, value)
                          }
                          placeholder='Enter'
                          maxLength={20}
                          disabled
                        />
                      </div>
                    </div>
                  </fieldset>
                )}
                <div className={style['bottom-bar']}>
                  <TooltipWrapper
                    tooltipSection={TOOLTIP_SECTIONS.PageAction}
                    tooltipKey='Show/Hide more optional fields'
                  >
                    <i
                      role='button'
                      className={classNames('link', style['toggle-link'])}
                      onClick={() =>
                        formik.setFieldValue(
                          `showOptional`,
                          !formik.values.showOptional,
                        )
                      }
                    >
                      {`${
                        formik.values.showOptional ? 'Hide' : 'Show'
                      } more optional fields`}
                    </i>
                  </TooltipWrapper>
                  <TooltipWrapper
                    tooltipSection={TOOLTIP_SECTIONS.Common}
                    tooltipKey='Required field'
                  >
                    <span className={style['required']}>* Required field</span>
                  </TooltipWrapper>
                </div>
              </div>
            </div>
            {/* Add company link */}
          </div>
          <div className={style['footer']}>
            {/* Submit link,it change the flag of submit */}
            <div className={style['buttons']}>
              <TooltipWrapper
                tooltipSection={TOOLTIP_SECTIONS.PageAction}
                tooltipKey='Save and complete'
              >
                <button
                  type='submit'
                  className='primary'
                  onClick={() => {
                    setSelectedSubmitButton('Save and complete');
                    setissubmit(true);
                    setSubmitToast(true);
                  }}
                  disabled={submitting}
                >
                  Save and complete
                  {submitting &&
                  selectedSubmitButton === 'Save and complete' ? (
                    <LoaderIndicator
                      className='button-loading'
                      loading={true}
                    />
                  ) : null}
                </button>
              </TooltipWrapper>
              {!id && (
                <TooltipWrapper
                  tooltipSection={TOOLTIP_SECTIONS.PageAction}
                  tooltipKey='Save and add another asset'
                >
                  <button
                    type='submit'
                    className='primary'
                    onClick={() => {
                      setSelectedSubmitButton('Save and add another asset');
                      setissubmit(true);
                      setSubmitToast(true);
                      setIsSaveReset(true);
                    }}
                    disabled={submitting}
                  >
                    Save and add another asset
                    {submitting &&
                    selectedSubmitButton === 'Save and add another asset' ? (
                      <LoaderIndicator
                        className='button-loading'
                        loading={true}
                      />
                    ) : null}
                  </button>
                </TooltipWrapper>
              )}
              <TooltipWrapper
                tooltipSection={TOOLTIP_SECTIONS.PageAction}
                tooltipKey='Cancel'
              >
                <button
                  type='button'
                  className='button secondary'
                  onClick={() => navigate(NAVIGATE)}
                  disabled={submitting}
                >
                  Cancel
                </button>
              </TooltipWrapper>
            </div>
          </div>
        </form>
      </div>
      <DiscardChangeConfirmModal
        initialData={{}}
        inputData={{}}
        navBlock={navBlock}
      />

      <Loader
        isOpen={
          fetchingCompanies ||
          fetchingAllLegalEntities ||
          fetchingAllProperties ||
          fetchingAllAssessors ||
          fetchingAsset
        }
      />
    </>
  );
}

const mapStateToProps = (state: ReduxState) => ({
  states: state.states,
});

export default connect(mapStateToProps)(InventorySetup);
